import '../../../styles/docs.scss';
import deliveredStarRelationships from '../../../images/DeliveredStarRelationships.drawio.svg';
import deliveredSignalRelationships from '../../../images/DeliveredSignalRelationships.drawio.svg';
import atlasStarRelationships from '../../../images/AtlasStarRelationships.drawio.svg';
import atlasSignalRelationships from '../../../images/AtlasSignalRelationships.drawio.svg';
import React, {ReactNode, useState} from "react";
import {Modal, Typography, Space, Divider} from 'antd';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

export const HelpButton: React.FC<{title: string, children: ReactNode, width?: number}> = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = () => setModalOpen(false);
  const openModal = () => setModalOpen(true);
  return (
    <>
      <Modal
        title={props.title}
        open={modalOpen}
        onClose={closeModal}
        onOk={closeModal}
        onCancel={closeModal}
        width={props.width}
      >
        {props.children}
      </Modal>
      <FontAwesomeIcon
        icon="fa-regular fa-square-question"
        onClick={openModal}
        className="help-icon"
      />
    </>
  );
}


export const DeliveredStarDoc: React.FC = () => {
  return (
    <>
      <Divider />
      <Typography.Title level={5}>Overview</Typography.Title>
      <Typography.Paragraph>
        Delivered Stars represent stellar sources which have been 'delivered'
        from an exoplanet search pipeline. These sources are derived from
        TIC, GAIA, and/or potentially other astrophysical catalogs. Their
        properties describe essential parameters used for exoplanet propery
        derivation, vetting and triage, and/or follow up observations.
      </Typography.Paragraph>

      <Typography.Paragraph>
        Similar to Delivered Signals; the properties described on Delivered
        Stars are static and are representative of the parameters used during
        pipeline processing.
      </Typography.Paragraph>
      <Divider />
      <Typography.Title level={5}>File Associations</Typography.Title>
      <Typography.Paragraph>
        Files associated with Delivered Stars are meant to describe the
        entire star system or at not limited to one signal. (i.e. Summary
        reports are not associated with Stars, only signals. Full reports
        are assigned to stars.)
      </Typography.Paragraph>
      <Divider />
      <Typography.Title level={5}>Atlases</Typography.Title>
      <Typography.Paragraph>
        As with Delivered Signals, Delivered Stars are associated with an
        Atlas in order to keep track of similar stellar sources across
        multiple deliveries over time. This matching is done via stellar
        identifiers.
      </Typography.Paragraph>
      <Divider />
      <img src={deliveredStarRelationships} className="tev-help-diagram" />
      <Divider />
      <Space direction="horizontal">
        <p>See Deliveries</p>
        <p>See Delivered Signals</p>
        <p>See Atlas System</p>
      </Space>
    </>
  );
};

export const DeliveredSignalDoc: React.FC = () => {
  return (
    <>
      <Divider />
      <Typography.Title level={5}>Overview</Typography.Title>
      <Typography.Paragraph>
        Delivered Signals represent a potential exoplanet candidate. These
        objects are equivalent to Threshold-Crossing-Events (TCEs).
        Properties on the Delivered Signal can be derived from a multitude
        of sources such as the host star, a Box-Least-fit-Search (BLS), or
        higher order model fits such as a simple trapezoidal model fit or
        more complex limb-darkening models.
      </Typography.Paragraph>
      <Typography.Paragraph>
        Similar to Delivered Stars; the properties described on Delivered
        Signals are static and are representative of the parameters used during
        pipeline processing.
      </Typography.Paragraph>
      <Divider />
      <Typography.Title level={5}>File Associations</Typography.Title>
      <Typography.Paragraph>
        Files associated with Delivered Signals are meant to describe the
        reports limited to that signal. (i.e. Summary
        reports. Full reports are assigned to stars and are not assigned to
        individual signals.)
      </Typography.Paragraph>
      <Divider />
      <Typography.Title level={5}>Atlases</Typography.Title>
      <Typography.Paragraph>
        As with Delivered Stars, Delivered Stars are associated with an
        Atlas in order to keep track of similar signals across
        multiple deliveries over time. This process is not as straight
        forward as stars, which are done through a simple ID crossmatch.
        Signals must be matched via signal ephemeris. For TOI processing
        matches to Atlases may be human curated by vetting feedback.
      </Typography.Paragraph>
      <Divider />
      <Typography.Title level={5}>Metadata</Typography.Title>
      <Typography.Paragraph>
        Unlike Delivered Stars and Atlases, Delivered Signals may have
        certain metadata associated with them (these changes were introduced
        with the TCE catalog/TEV3 and thus may not be guaranteed to be
        populated). Abstractly, this metadata describes attempts at
        classification into vetting categories, additional model fits and
        astrophysical parameters, and the initial search for the candidate.
      </Typography.Paragraph>
      <Divider />
      <img src={deliveredSignalRelationships} className="tev-help-diagram" />
      <Divider />
      <Space direction="horizontal">
        <p>See Deliveries</p>
        <p>See Delivered Stars</p>
        <p>See Atlas System</p>
        <p>See Planet Scores</p>
        <p>See Planet Searches</p>
        <p>See Planet Fits</p>
      </Space>
    </>
  );
};


export const AtlasStarDoc: React.FC = () => {
  return (
    <>

      <Divider />
      <Typography.Title level={5}>Overview</Typography.Title>
      <Typography.Paragraph>
        Atlas Stars are part of the Atlas system for describing stars.
        These differ from Deliveries as Atlas objects are "timeless"
        meaning they do not describe results from exoplanet search
        pipelines but rather are the mapping of a multitude of those
        results to one objects. An Atlas Star will be a singular map
        to potentially many Delivery Stars; all matched via stellar
        ids.
      </Typography.Paragraph>

      <Typography.Paragraph>
        Parameters on Atlas stars may not reflect individual
        delivery parameters. Atlas' primary purpose is to choose the
        most accurate information provided by any delivery. This
        process is manual, and often signals near TOI release
        are vetted and undergo quality assurance.
      </Typography.Paragraph>
      <Divider />
      <Typography.Title level={5}>File Associations</Typography.Title>
      <Typography.Paragraph>
        No files are directly associated with any Atlas. However,
        entrypoints are provided to allow users to see all reports
        on an Atlas by referencing any deliveries associated with
        any atlas.
      </Typography.Paragraph>
      <Divider />
      <img src={atlasStarRelationships} className="tev-help-diagram" />
      <Divider />
      <Space direction="horizontal">
        <p>See Delivered Signals</p>
        <p>See Atlas System</p>
      </Space>
    </>
  );
};

export const AtlasSignalDoc: React.FC = () => {
  return (
    <>
      <Divider />
      <Typography.Title level={5}>Overview</Typography.Title>
      <Typography.Paragraph>
        Atlas Signals represent a timeless description of exoplanet
        candidates. Delivered Signals (TCEs) are matched to a single atlas
        through ephemeris matching utilizing the host star's id, transit
        period and transit epoch information.

        TOI objects are assigned to Atlas Signals and Stars.
      </Typography.Paragraph>

      <Typography.Paragraph>
        Parameters on Atlas Signals may not reflect individual
        delivery parameters. Atlas' primary purpose is to choose the
        most accurate information provided by any delivery. This
        process is manual, and often signals near TOI release
        are vetted and undergo quality assurance.
      </Typography.Paragraph>
      <Divider />
      <Typography.Title level={5}>File Associations</Typography.Title>
      <Typography.Paragraph>
        No files are directly associated with any Atlas. However,
        entrypoints are provided to allow users to see all reports
        on an Atlas by referencing any deliveries associated with
        any atlas.
      </Typography.Paragraph>
      <Divider />
      <img src={atlasSignalRelationships} className="tev-help-diagram" />
      <Divider />
      <Space direction="horizontal">
        <p>See Delivered Signals</p>
        <p>See Atlas System</p>
        <p>See TOI System</p>
      </Space>
    </>
  );
};

export const RESOURCETOHELPLOOKUP = {
  "DeliveredSignal": (
    <HelpButton title="Delivered Signal Quick Doc">
      <DeliveredSignalDoc />
    </HelpButton>
  ),
  "DeliveredStar": (
    <HelpButton title="Delivered Star Quick Doc">
      <DeliveredStarDoc />
    </HelpButton>
  ),
  "AtlasStar": (
    <HelpButton title="Atlas Star Quick Doc">
      <AtlasStarDoc />
    </HelpButton>
  ),
  "AtlasSignal": (
    <HelpButton title="Atlas Signal Quick Doc">
      <AtlasSignalDoc />
    </HelpButton>
  ),
};
